import { IconBook2, IconLogout, IconSchool, IconSettings, IconShoppingCart, IconVideo } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { type ComponentType, type FC } from "react"
import { Link, useLocation } from "react-router"

import { type FCC } from "@forento/ui/types/react"
import { cn } from "@forento/ui/utilities/tailwind"

import { useUserContext } from "~/context/user"
import trpc, { query } from "~/utilities/trpc"

import logo from "~/assets/logo.png"

const Layout: FCC<{ className?: string }> = ({ children, className }) => {
	const userContext = useUserContext()
	const queryClient = useQueryClient()
	const utilities = query.useUtils()

	const signOut = async () => {
		await queryClient.cancelQueries({ type: "active" })
		utilities.me.setData(undefined, null)
		try {
			await trpc.signOut.mutate()
		} catch (error) {
			console.error(error)
			await userContext.reload()
		}
	}

	return (
		<div className="flex min-h-screen">
			<nav className="border-component-border bg-component-background shrink-0 basis-64 border-r px-4 py-6 shadow">
				<img src={logo} alt="Bokaderos logotyp" className="w-full px-4" />
				<div className="mt-2 mb-10 w-full px-4">
					<p className="text-center text-xs font-semibold tracking-wide text-slate-500">Kursskapare</p>
				</div>
				<NavLink to="/courses" label="Kurser" icon={IconBook2} />
				<NavLink to="/videos" label="Videor" icon={IconVideo} />
				<NavLink to="/sales" label="Försäljning" icon={IconShoppingCart} />
				<NavLink to="/build-your-own" label="Bygg din egna akademi" icon={IconSchool} />
				<NavLink to="/user/settings" label="Inställningar" icon={IconSettings} />
				<Button onClick={signOut} label="Logga ut" icon={IconLogout} />
			</nav>
			<div className={cn("flex shrink grow basis-0 flex-col p-6", className)}>{children}</div>
		</div>
	)
}

type NavLinkProps = { to: string; label: string; icon: ComponentType<{ className?: string }> }
const NavLink: FC<NavLinkProps> = ({ to, label, icon: Icon }) => {
	const location = useLocation()

	const isActive = location.pathname === to

	return (
		<Link
			to={to}
			className={cn(
				"group duration-all flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium duration-200",
				isActive
					? "bg-primary shadow-primary/25 text-white shadow-md"
					: "text-slate-700 hover:bg-slate-200 hover:text-slate-900",
			)}
		>
			<Icon
				className={cn(
					"size-5 transition-colors",
					isActive ? "text-white" : "text-slate-500 group-hover:text-slate-700",
				)}
			/>
			{label}
		</Link>
	)
}

type ButtonProps = { label: string; icon: ComponentType<{ className?: string }>; onClick(): void }
const Button: FC<ButtonProps> = ({ label, icon: Icon, onClick }) => {
	return (
		<button
			type="button"
			onClick={onClick}
			className="group duration-all flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 duration-200 hover:bg-slate-200 hover:text-slate-900"
		>
			<Icon className={"size-5 text-slate-500 transition-colors group-hover:text-slate-700"} />
			{label}
		</button>
	)
}

export default Layout
